const MAX_STRING_LENGTH = 240;
const MAX_OBJECT_KEYS = 50;
const MAX_ARRAY_ITEMS = 6;
const MAX_DEPTH = 4;

export const SENSITIVE_BROWSER_KEY_PATTERN =
  /(?:^|_|\b)(audit|verificationAudit|provider|model|engine|engineFlags|threshold|score|confidence|attempt|timing|latency|deterministic|deterministicScore|deterministicThreshold|deterministicModelVersion|productionModel|faceMatchPrimaryModel|faceMatchFallbackModel|faceMatchAttempts|faceMatchTimingsMs|ocr|fraud|debug|stack|trace|raw|sessionToken|handoffToken|apiKey|authorization|cookie|query|url|uri|href|externalUserId|bestSelfie|biometric)(?:$|_|\b)/i;

export const SENSITIVE_BROWSER_TEXT_PATTERN =
  /(?:https?:\/\/|wss?:\/\/|sessionToken=|handoffToken=|[?&]token=|apiKey=|(?:^|["'\s{,/])(?:audit|verificationAudit|provider|model|engine|engineFlags|threshold|score|confidence|attempt|timing|latency|deterministic|deterministicScore|deterministicThreshold|deterministicModelVersion|deterministicEngine|productionModel|faceMatchPrimaryModel|faceMatchFallbackModel|faceMatchPrimaryProvider|faceMatchFallbackProvider|faceMatchAttempts|faceMatchTimingsMs|provider_audit|raw_llm_response|ocr|fraud|debug|stack|stackTrace|raw|sessionToken|apiKey|authorization|cookie|query|externalUserId|bestSelfie|biometric|qwen|openai|gpt|insightface|yunet|arcface)(?:$|["'\s}:,/.?&=+-]))/i;

const SENSITIVE_BROWSER_KEY_FRAGMENTS = [
  'audit',
  'verificationaudit',
  'provider',
  'model',
  'engine',
  'engineflags',
  'threshold',
  'score',
  'confidence',
  'attempt',
  'timing',
  'latency',
  'deterministic',
  'productionmodel',
  'facematchprimarymodel',
  'facematchfallbackmodel',
  'facematchprimaryprovider',
  'facematchfallbackprovider',
  'facematchattempts',
  'facematchtimingsms',
  'ocr',
  'fraud',
  'debug',
  'stack',
  'trace',
  'raw',
  'sessiontoken',
  'handofftoken',
  'apikey',
  'authorization',
  'cookie',
  'query',
  'url',
  'uri',
  'href',
  'externaluserid',
  'bestselfie',
  'biometric',
];

type BrowserSafePrimitive = string | number | boolean | null;
type BrowserSafeValue =
  | BrowserSafePrimitive
  | BrowserSafeValue[]
  | { [key: string]: BrowserSafeValue };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isSensitiveBrowserKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return (
    SENSITIVE_BROWSER_KEY_PATTERN.test(key) ||
    SENSITIVE_BROWSER_KEY_FRAGMENTS.some((fragment) =>
      normalizedKey.includes(fragment)
    )
  );
};

export const containsSensitiveBrowserText = (value: string): boolean =>
  SENSITIVE_BROWSER_TEXT_PATTERN.test(value);

const sanitizeString = (value: string): string => {
  if (containsSensitiveBrowserText(value)) {
    return '[REDACTED]';
  }

  if (value.length > MAX_STRING_LENGTH) {
    return `${value.slice(0, MAX_STRING_LENGTH)}...`;
  }

  return value;
};

const sanitizeError = (error: Error): { name: string; message: string } => ({
  name: error.name || 'Error',
  message: containsSensitiveBrowserText(error.message)
    ? 'Browser log detail redacted'
    : sanitizeString(error.message),
});

export function redactForBrowserLog(
  value: unknown,
  depth: number = 0
): BrowserSafeValue | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (value instanceof Error) {
    return sanitizeError(value);
  }

  if (Array.isArray(value)) {
    if (depth >= MAX_DEPTH) {
      return `[Array(${value.length}) redacted]`;
    }

    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => redactForBrowserLog(item, depth + 1) ?? null);
  }

  if (!isRecord(value)) {
    return String(value);
  }

  const entries = Object.entries(value);
  if (depth >= MAX_DEPTH || entries.length > MAX_OBJECT_KEYS) {
    return '[Object redacted]';
  }

  const safe: Record<string, BrowserSafeValue> = {};
  for (const [key, item] of entries) {
    if (isSensitiveBrowserKey(key)) {
      continue;
    }

    const redacted = redactForBrowserLog(item, depth + 1);
    if (redacted !== undefined) {
      safe[key] = redacted;
    }
  }

  return safe;
}

export function sanitizeBrowserMetadata(
  value: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!value) {
    return undefined;
  }

  const redacted = redactForBrowserLog(value);
  return isRecord(redacted) ? redacted : undefined;
}
